import type { ListBuilderConfiguration } from '../types';

/**
 * Purchase Order Detail (Line Items) list configuration
 * Shows line items within a purchase order with quantities, costs, and expected dates
 */
export const PURCHASE_ORDER_DETAIL_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-purchase-order-details-all',
			cacheTTL: 600000,
			name: 'All Purchase Order Line Items',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'item-number',
					fields: [
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item_external_id',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'link',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-item-number', text: 'Item #' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'item_external_id',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'item-description',
					fields: [
						{
							id: 'item-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item_description',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'primary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-description', text: 'Description' }],
					minWidth: 250,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'item_description',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'expected-date',
					fields: [
						{
							id: 'expected-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'expectedReceiptDate',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-expected-date', text: 'Expected Date' }],
					minWidth: 130,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'expectedReceiptDate',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-ordered',
					fields: [
						{
							id: 'qty-ordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityOrdered',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-qty-ordered', text: 'Qty Ordered' }],
					minWidth: 100,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityOrdered',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-received',
					fields: [
						{
							id: 'qty-received-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityReceived',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'success',
								},
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-qty-received', text: 'Qty Received' }],
					minWidth: 100,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityReceived',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-backordered',
					fields: [
						{
							id: 'qty-backordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityBackordered',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'warning',
								},
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-backordered', text: 'Backordered' }],
					minWidth: 100,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityBackordered',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'unit-cost',
					fields: [
						{
							id: 'unit-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'cost',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [{ id: 'h-unit-cost', text: 'Unit Cost' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'cost',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'line-total',
					fields: [
						{
							id: 'line-total-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lineTotal',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
								fontWeight: 'bold',
							},
							displayStyle: 'currency',
						},
					],
					headers: [{ id: 'h-total', text: 'Total' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'lineTotal',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					// PD-14586: per-line short-close. The callback (wired by
					// PurchaseOrderLineItemsTab) guards already-cancelled /
					// nothing-remaining lines, since row actions have no
					// per-row visibility predicate.
					id: 'actions',
					headers: [{ id: 'h-actions', text: 'Actions' }],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'cancel-remaining-action',
									label: 'Cancel Remaining',
									iconName: 'ban',
									callbackName: 'cancelRemainingLine',
								},
							],
						},
					],
					sortable: false,
					width: 90,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
			],
			description: 'List of purchase order line items',
			defaultSortId: 'column-item-description-asc',
			requiredFields: [
				'purchaseOrderDetail._id',
				'purchaseOrderDetail.externalId',
				'purchaseOrderDetail.quantityOrdered',
				'purchaseOrderDetail.quantityReceived',
				'purchaseOrderDetail.quantityBackordered',
				'purchaseOrderDetail.cost',
				'purchaseOrderDetail.expectedReceiptDate',
				'purchaseOrderDetail.cancelledDate',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/purchase-orders/:purchaseOrderId/details/:id',
			objectLabel: 'Purchase Order Detail',
			objectTable: 'purchase_order_detail',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'item_external_id', fieldLabel: 'Item #' },
				{ fieldName: 'item_description', fieldLabel: 'Description' },
				{ fieldName: 'expectedReceiptDate', fieldLabel: 'Expected Date' },
				{ fieldName: 'quantityOrdered', fieldLabel: 'Qty Ordered' },
				{ fieldName: 'quantityReceived', fieldLabel: 'Qty Received' },
				{
					fieldName: 'quantityBackordered',
					fieldLabel: 'Qty Backordered',
				},
				{ fieldName: 'cost', fieldLabel: 'Unit Cost' },
			],
		},
	],
	tableSorts: [
		{
			id: 'item-description',
			name: 'Description',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'item_description',
				},
			],
			description: 'Sort by item description',
		},
		{
			id: 'expected-date',
			name: 'Expected Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'expectedReceiptDate',
				},
			],
			description: 'Sort by expected date (newest first)',
		},
		{
			id: 'qty-ordered',
			name: 'Qty Ordered',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'quantityOrdered',
				},
			],
			description: 'Sort by quantity ordered (highest first)',
		},
		{
			id: 'unit-cost',
			name: 'Unit Cost',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'cost',
				},
			],
			description: 'Sort by unit cost (highest first)',
		},
	],
};
